import React from 'react';

function Privacy() {
  return (
    <main>
      <div className="page-container">
        <h1>Privacy Policy</h1>
        <p><strong>Effective Date:</strong> January 24, 2026</p>
        <p><strong>Last Updated:</strong> January 24, 2026</p>

        <p style={{ fontSize: '1.1rem', color: '#555', marginTop: '1.5rem' }}>
          SwiftBreak ("we", "our", or "us") is committed to protecting your privacy. 
          This Privacy Policy explains how we handle information in connection with our 
          mobile application SwiftBreak (the "App").
        </p>

        <h2>Information We Collect</h2>

        <h3>Data We DO NOT Collect</h3>
        <p>
          SwiftBreak is designed with privacy in mind. We want to be clear about what we 
          <strong> do NOT</strong> collect:
        </p>
        <ul>
          <li>We do not collect any personally identifiable information (PII)</li>
          <li>We do not collect your name, email address, or contact information</li>
          <li>We do not collect your device identifiers (IDFA, advertising ID, etc.)</li>
          <li>We do not track your location</li>
          <li>We do not access your contacts, photos, or other personal data</li>
          <li>We do not use third-party analytics or tracking services</li>
          <li>We do not collect any data about your usage patterns or behaviors</li>
        </ul>

        <h3>Data Stored Locally on Your Device</h3>
        <p>
          The App stores the following information <strong>locally on your device only</strong>:
        </p>
        <ul>
          <li><strong>Game Statistics:</strong> Your match history, win counts, shooting performance metrics</li>
          <li><strong>Achievement Progress:</strong> Which achievements you have unlocked</li>
          <li><strong>Settings and Preferences:</strong> Your chosen table theme, audio settings, gameplay preferences</li>
          <li><strong>Tutorial Status:</strong> Whether you have completed the tutorial</li>
        </ul>
        <p>
          <strong>Important:</strong> All this data remains exclusively on your device. We do not 
          have access to it, it is not transmitted to our servers, and we cannot view or retrieve it.
        </p>

        <h2>How We Use Your Information</h2>
        <p>
          Since we do not collect any personal information, we do not use your information in any way. 
          The data stored locally on your device is used solely to:
        </p>
        <ul>
          <li>Provide gameplay functionality</li>
          <li>Save your progress and achievements</li>
          <li>Remember your settings and preferences</li>
          <li>Enhance your gaming experience</li>
        </ul>
        <p>
          This data never leaves your device and is not shared with anyone.
        </p>

        <h2>Data Sharing and Disclosure</h2>
        <p>
          We do not share, sell, rent, or trade any information with third parties because we do not 
          collect any information. SwiftBreak:
        </p>
        <ul>
          <li>Does not contain advertisements</li>
          <li>Does not integrate with social media platforms</li>
          <li>Does not use analytics services</li>
          <li>Does not track user behavior</li>
          <li>Does not transmit any data to external servers</li>
        </ul>

        <h2>Data Security</h2>
        <p>
          Your locally stored game data is protected by your device's built-in security features, 
          including:
        </p>
        <ul>
          <li>iOS app sandboxing (data isolation from other apps)</li>
          <li>Device encryption (if enabled on your device)</li>
          <li>iOS security updates and protections</li>
        </ul>
        <p>
          Since no data is transmitted over the internet, there is no risk of interception or 
          unauthorized access during transmission.
        </p>

        <h2>Data Retention and Deletion</h2>

        <h3>How Long We Keep Your Data</h3>
        <p>
          Your game data remains on your device until you:
        </p>
        <ul>
          <li>Delete the SwiftBreak app from your device</li>
          <li>Clear the app's data through iOS Settings</li>
          <li>Perform a factory reset of your device</li>
        </ul>

        <h3>How to Delete Your Data</h3>
        <p>
          To completely remove all SwiftBreak data from your device:
        </p>
        <ol>
          <li>Long-press the SwiftBreak app icon on your home screen</li>
          <li>Tap "Remove App"</li>
          <li>Select "Delete App" (this removes both the app and all associated data)</li>
        </ol>
        <p>
          Alternatively, you can go to Settings &gt; General &gt; iPhone Storage &gt; SwiftBreak &gt; 
          Delete App.
        </p>

        <h2>Children's Privacy</h2>
        <p>
          SwiftBreak is suitable for users of all ages, including children. Since we do not collect 
          any personal information from any users, we do not collect personal information from 
          children under 13 (or equivalent minimum age in your jurisdiction).
        </p>
        <p>
          The App complies with the Children's Online Privacy Protection Act (COPPA) and similar 
          regulations worldwide by design - we simply don't collect any data that would require 
          parental consent.
        </p>

        <h2>Third-Party Services</h2>
        <p>
          SwiftBreak does not integrate with any third-party services, including:
        </p>
        <ul>
          <li>No advertising networks</li>
          <li>No analytics services (Google Analytics, Firebase, etc.)</li>
          <li>No social media integration</li>
          <li>No cloud storage services</li>
          <li>No payment processors (the app is free with no in-app purchases)</li>
        </ul>
        <p>
          The only external service used is Apple's App Store for distribution, which is governed 
          by Apple's own privacy policy.
        </p>

        <h2>International Users</h2>
        <p>
          SwiftBreak is available worldwide. Since no data is collected or transmitted, there are 
          no international data transfer concerns. All data remains on your device regardless of 
          your location.
        </p>

        <h2>Changes to This Privacy Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. If we make any material changes, we will:
        </p>
        <ul>
          <li>Update the "Last Updated" date at the top of this policy</li>
          <li>Notify users through the App Store update notes</li>
          <li>Post the updated policy on this page</li>
        </ul>
        <p>
          We encourage you to review this Privacy Policy periodically. Your continued use of the App 
          after any changes indicates your acceptance of the updated policy.
        </p>

        <h2>Your Rights</h2>
        <p>
          Depending on your location, you may have certain rights regarding your personal data under 
          laws such as GDPR (Europe), CCPA (California), or similar regulations. However, since 
          SwiftBreak does not collect any personal data, most of these rights are not applicable:
        </p>
        <ul>
          <li><strong>Right to Access:</strong> Not applicable - we have no data about you to access</li>
          <li><strong>Right to Deletion:</strong> You can delete all local data by uninstalling the app</li>
          <li><strong>Right to Portability:</strong> Not applicable - we do not collect or store your data</li>
          <li><strong>Right to Object:</strong> Not applicable - we do not process your data</li>
          <li><strong>Right to Opt-Out:</strong> Not applicable - we do not collect data to opt out from</li>
        </ul>

        <h2>California Privacy Rights</h2>
        <p>
          Under the California Consumer Privacy Act (CCPA), California residents have specific rights. 
          However, since SwiftBreak does not collect personal information, the following applies:
        </p>
        <ul>
          <li>We do not sell personal information (because we don't collect it)</li>
          <li>We do not share personal information for cross-context behavioral advertising</li>
          <li>We do not collect sensitive personal information</li>
        </ul>

        <h2>European Union Users (GDPR)</h2>
        <p>
          For users in the European Union, SwiftBreak complies with the General Data Protection 
          Regulation (GDPR). Since we do not collect personal data:
        </p>
        <ul>
          <li>No data processing occurs that requires legal basis</li>
          <li>No data is transferred outside the EU</li>
          <li>No profiling or automated decision-making occurs</li>
          <li>No consent is required for data processing</li>
        </ul>

        <h2>Contact Us</h2>
        <p>
          If you have any questions, concerns, or requests regarding this Privacy Policy or our 
          privacy practices, please contact us:
        </p>
        <div className="contact-info">
          <p><strong>Email:</strong> <a href="mailto:wsmslgh@gmail.com">wsmslgh@gmail.com</a></p>
          <p><strong>App Name:</strong> SwiftBreak</p>
          <p><strong>Response Time:</strong> We aim to respond to all inquiries within 48 hours</p>
        </div>

        <h2>Transparency Commitment</h2>
        <p>
          We believe in complete transparency about data practices. Here's our commitment:
        </p>
        <ul>
          <li>✅ <strong>No data collection</strong> - We don't collect any personal information</li>
          <li>✅ <strong>No tracking</strong> - We don't track your behavior or usage</li>
          <li>✅ <strong>No ads</strong> - No advertising networks or trackers</li>
          <li>✅ <strong>No third parties</strong> - Your data isn't shared with anyone</li>
          <li>✅ <strong>Local only</strong> - All game data stays on your device</li>
          <li>✅ <strong>Open communication</strong> - Contact us anytime with questions</li>
        </ul>

        <h2>Summary</h2>
        <p>
          <strong>TL;DR:</strong> SwiftBreak respects your privacy completely. We don't collect, 
          store, or share any of your personal information. All your game data (statistics, 
          achievements, settings) stays on your device and never leaves it. No ads, no tracking, 
          no third parties. Just a fun pool game that respects your privacy.
        </p>

        <hr style={{ margin: '2rem 0', border: 'none', borderTop: '1px solid #ddd' }} />

        <p style={{ fontSize: '0.9rem', color: '#999' }}>
          This Privacy Policy is provided in English. If you require this policy in another language, 
          please contact us at <a href="mailto:wsmslgh@gmail.com">wsmslgh@gmail.com</a>.
        </p>

        <p style={{ fontSize: '0.9rem', color: '#999' }}>
          By using SwiftBreak, you acknowledge that you have read and understood this Privacy Policy.
        </p>
      </div>
    </main>
  );
}

export default Privacy;
